import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import type { LegalCaseListItem } from '../types/LegalCase';

export default function useLegalCasePageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('-openedAt');
    const [pole, setPole] = useState('');
    const [caseType, setCaseType] = useState('');
    const [status, setStatus] = useState('');
    const [rows, setRows] = useState<LegalCaseListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const size = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    const [selectedCase, setSelectedCase] = useState<LegalCaseListItem | null>(null);
    const [caseDetail, setCaseDetail] = useState<any>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await recoveryApi.getLegalCases({
                page,
                size,
                sort: sort || undefined,
                search: search || undefined,
                pole: pole || undefined,
                type: caseType || undefined,
                status: status || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, search, sort, pole, caseType, status, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = async (row: LegalCaseListItem) => {
        setSelectedCase(row);
        setIsLoadingDetail(true);
        try {
            const detail = await recoveryApi.getLegalCaseView(row.id);
            setCaseDetail(detail);
        } catch {
            setCaseDetail(null);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const closePanel = () => {
        setSelectedCase(null);
        setCaseDetail(null);
    };

    return {
        rows, total, page, setPage, size,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, search, setSearch,
        sort, setSort,
        pole, setPole,
        caseType, setCaseType,
        status, setStatus,
        refresh, handleRowClick,
        selectedCase, caseDetail, isLoadingDetail, closePanel,
        apiInfo,
    };
}
